/**
 * The defeat sequence.
 *
 * Losing used to be bureaucratic: lives hit zero, the driver flipped the phase,
 * and a modal appeared over a board frozen mid-step. Nothing on screen said the
 * run had ended — the sheet said it, in text.
 *
 * This is the moment instead, and it is built out of one shape: **fast, then
 * slow**. The exit portal detonates on a single frame, the board's survivors
 * flash-vaporise in a cascade over the next fifth of a second, and then
 * everything stops and the tube is left to cool. The cooling is not a fade
 * painted over the frame — it is the phosphor persistence pass, which is
 * physically modelled per channel, driven with its trail-length term cranked to
 * seven times normal. The last burst genuinely hangs on the glass and drains
 * out through green the way a real long-persistence tube does, and the sheet
 * rises out of that rather than interrupting it.
 *
 * Clock policy: **sim ticks, never wall time.** The capture harness advances
 * frames synchronously and would otherwise photograph tick zero six times.
 *
 * Ownership: this file talks to the post chain through {@link DefeatPost}, a
 * two-method view of `renderer.post` that `main.ts` binds, and it never touches
 * the enemy pool directly — {@link DefeatFxHooks.vaporise} is the seam.
 */

import { VIEW } from '@/core/config';
import { ROLE } from '@/core/palette';
import type { PhosphorName } from '@/core/palette';
import type { ParticleSpawn, World } from '@/core/types';

const TAU = Math.PI * 2;

// ────────────────────────────────────────────────────────────── the clock ──

/*
 * Every beat below is a count of 60Hz simulation steps. They are written as
 * ticks rather than seconds because that is the unit the sequence is actually
 * advanced in, and a seconds-to-ticks conversion at the top of a step is one
 * more place for a capture to land half a frame off a photograph.
 */

/** Frames of detonation. Loud, white, over almost before it registers. */
const BLAST_TICKS = 10;
/** Frames the board takes to flash-vaporise, counted from the blast. */
const CASCADE_TICKS = 14;
/** Frame the afterglow starts opening on. */
const GLOW_OPEN = 14;
/** Frame the afterglow reaches full length. */
const GLOW_FULL = 44;
/** Frame the sheet is allowed to rise. Everything before it is the moment. */
const REVEAL_TICK = 108;
/** Frame the post chain is back exactly where it was found. */
const END_TICK = 152;

/**
 * Trail-length multiplier at the peak of the afterglow.
 *
 * `CRT.persistence` scales the seconds of decay charged per frame, so seven is
 * a seven-times-longer glow: the blast's white core, which normally clears in
 * about 200ms, hangs for the better part of a second and a half and drains out
 * through green. Higher reads as a smear rather than as phosphor — past about
 * ten the bimolecular tail stops falling inside the sequence at all and the
 * board is still lit when the sheet has finished rising.
 */
const GLOW_GAIN = 6;

/**
 * Trail cutoff luminance while the afterglow is open.
 *
 * `CRT.persistenceFloor` is 0.005 — low enough that *everything* on the tube
 * leaves a trail, which at six times the normal length turns the background
 * lattice into a green wash and buries the thing the player is supposed to be
 * looking at. Raised to here, the lattice and the corridor ribbon both fall
 * under the cut and stop trailing entirely, and what hangs on the glass is what
 * was actually burning. That is the difference between the board going quiet
 * and the board going soft.
 */
const GLOW_FLOOR = 0.16;

/** Bloom multiplier on the blast frames. The chain is HDR; it will carry it. */
const BLAST_BLOOM = 2.1;

// ────────────────────────────────────────────────────────────── the shapes ──

/** Sparks in the first detonation. */
const BLAST_SPARKS = 150;
/** Embers drifting through the afterglow, per tick, while it lasts. */
const EMBER_CHANCE = 0.55;
/** Keeps the detonation this far inside the buffer. See {@link DefeatFx.trigger}. */
const BLAST_INSET = 34;

// ─────────────────────────────────────────────────────────────────── seams ──

/** The three post-chain terms the sequence drives. Nothing else is touched. */
export type DefeatPostKey = 'persistence' | 'intensity' | 'persistenceFloor';

/**
 * A read/write view of the post chain.
 *
 * Deliberately not `PostStack` itself: the CRT chain is another subsystem, and
 * a sequence that imported it would be able to reach every uniform in the
 * renderer to set two of them.
 */
export interface DefeatPost {
  get(key: DefeatPostKey): number;
  set(key: DefeatPostKey, value: number): void;
}

export interface DefeatFxHooks {
  post: DefeatPost;
  /**
   * Blows the enemy at `index` off the board with its own death burst. Bound to
   * `Enemy.vaporiseAt`, which is the only thing that may write the pool.
   */
  vaporise(world: World, index: number): void;
  /** The sheet may rise. Fired exactly once, on {@link REVEAL_TICK}. */
  reveal(): void;
  /** The post chain is restored and the sim may stop. Fired exactly once. */
  done(): void;
}

// ───────────────────────────────────────────────────────────── spawn ring ──

/**
 * Pooled spawn descriptors, the same ring the enemy pool uses. A sequence that
 * allocated its own would do it on the one frame of the run with the least
 * headroom to spare.
 */
const RING = 512;
const ring: ParticleSpawn[] = new Array(RING);
for (let i = 0; i < RING; i++) {
  ring[i] = {
    x: 0, y: 0, vx: 0, vy: 0, life: 0, size: 1,
    colour: 'white', gain: 1, kind: 'spark', drag: 0, gravity: 0, spin: 0,
  };
}
let cursor = 0;

function emit(
  world: World, x: number, y: number, vx: number, vy: number,
  life: number, size: number, colour: PhosphorName,
  kind: ParticleSpawn['kind'], gain: number, drag: number, gravity: number,
): void {
  const p = ring[cursor];
  cursor = (cursor + 1) & (RING - 1);
  p.x = x; p.y = y; p.vx = vx; p.vy = vy;
  p.life = life; p.size = size; p.colour = colour; p.kind = kind;
  p.gain = gain; p.drag = drag; p.gravity = gravity; p.spin = 0;
  world.fx.particles.push(p);
}

const shockRing = new Array(16).fill(null)
  .map(() => ({ x: 0, y: 0, strength: 0, radius: 0, duration: 0 }));
let shockCursor = 0;

function shock(world: World, x: number, y: number, strength: number, radius: number, duration: number): void {
  const s = shockRing[shockCursor];
  shockCursor = (shockCursor + 1) % shockRing.length;
  s.x = x; s.y = y; s.strength = strength; s.radius = radius; s.duration = duration;
  world.fx.shockwaves.push(s);
}

// ───────────────────────────────────────────────────────────── the sequence ──

export class DefeatFx {
  readonly name = 'defeatFx';

  private readonly hooks: DefeatFxHooks;
  /** −1 when idle. Counts simulation steps from the detonation. */
  private tick = -1;
  private x = 0;
  private y = 0;
  /** Post values as they were found, restored exactly on {@link END_TICK}. */
  private basePersistence = 1;
  private baseIntensity = 1;
  private baseFloor = 0.005;
  private revealed = false;
  private readonly motionQuery: MediaQueryList | null;
  /** Own seeded stream, so ember scatter never perturbs the sim's `world.rng`. */
  private seed = 0x9e3779b9;

  constructor(hooks: DefeatFxHooks) {
    this.hooks = hooks;
    this.motionQuery = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
  }

  /** True while the sequence owes the board frames. */
  get running(): boolean {
    return this.tick >= 0;
  }

  /**
   * Ticks the sequence will run for, so the caller can hold the clock for
   * exactly that long and not a frame more.
   */
  get duration(): number {
    return this.reduced ? 4 : END_TICK;
  }

  /** The tick the sheet rises on. */
  get revealAt(): number {
    return this.reduced ? 2 : REVEAL_TICK;
  }

  private get reduced(): boolean {
    return this.motionQuery?.matches === true;
  }

  /**
   * Starts the sequence at the exit portal, which is the only place on the map
   * that means what just happened: the leak got through *there*.
   *
   * `x`/`y` override it — a caller that knows which body took the last life can
   * detonate on the body instead. Nothing does yet.
   */
  trigger(world: World, x = NaN, y = NaN): void {
    if (this.tick >= 0) return;
    this.tick = 0;
    this.revealed = false;
    this.seed = 0x9e3779b9;
    this.basePersistence = this.hooks.post.get('persistence');
    this.baseIntensity = this.hooks.post.get('intensity');
    this.baseFloor = this.hooks.post.get('persistenceFloor');

    if (Number.isNaN(x) || Number.isNaN(y)) {
      this.findExit(world);
    } else {
      this.x = x;
      this.y = y;
    }
  }

  /**
   * The last point of the corridor that is actually on screen.
   *
   * Not the exit itself, and the difference is the whole effect: every shipped
   * map runs its exit tail off the edge of the buffer on purpose, so the
   * centreline's last sample sits 60-ish pixels outside the picture. Detonating
   * there put the largest explosion in the game off-screen — measured on act
   * III's corridor, where the blast lit the right margin and nothing else. So
   * the centreline is walked backwards, one sample at a time, until it re-enters
   * the frame with room for the fireball.
   */
  private findExit(world: World): void {
    const pts = world.track.points;
    this.x = VIEW.width * 0.5;
    this.y = VIEW.height * 0.5;
    for (let i = pts.length - 2; i >= 0; i -= 2) {
      const px = pts[i], py = pts[i + 1];
      if (px < BLAST_INSET || px > VIEW.width - BLAST_INSET) continue;
      if (py < BLAST_INSET || py > VIEW.height - BLAST_INSET) continue;
      this.x = px;
      this.y = py;
      return;
    }
  }

  /**
   * Puts the post chain back and forgets the sequence. Called by `Session.start`
   * through `main.ts` — a restart during the afterglow would otherwise leave the
   * new run playing under a seven-times trail.
   */
  cancel(): void {
    if (this.tick < 0) return;
    this.tick = -1;
    this.revealed = false;
    this.hooks.post.set('persistence', this.basePersistence);
    this.hooks.post.set('intensity', this.baseIntensity);
    this.hooks.post.set('persistenceFloor', this.baseFloor);
  }

  step(_dt: number, world: World): void {
    if (this.tick < 0) return;
    const t = this.tick++;

    if (this.reduced) {
      // A single flash and out. The afterglow is a second-long ghost of the
      // whole board and there is no honest way to soften it into something a
      // reduced-motion reader asked for, so it is not played at all.
      if (t === 0) {
        emit(world, this.x, this.y, 0, 0, 0.3, 90, 'white', 'ring', 3.2, 0, 0);
        this.cascade(world, CASCADE_TICKS - 1);
      }
      if (t >= 2 && !this.revealed) { this.revealed = true; this.hooks.reveal(); }
      if (t >= 4) { this.tick = -1; this.hooks.done(); }
      return;
    }

    if (t === 0) this.detonate(world);
    if (t > 0 && t < BLAST_TICKS) this.aftershock(world, t);
    if (t < CASCADE_TICKS) this.cascade(world, t);
    this.drivePost(t);
    if (t >= GLOW_OPEN && t < REVEAL_TICK) this.embers(world, t);

    if (t >= this.revealAt && !this.revealed) {
      this.revealed = true;
      this.hooks.reveal();
    }
    if (t >= END_TICK) {
      this.tick = -1;
      this.hooks.done();
    }
  }

  /**
   * Frame one. A white-hot core over the portal, a hard shell of sparks in the
   * portal's own red, a heavy lattice punch and the biggest camera hit the game
   * ever throws.
   */
  private detonate(world: World): void {
    const { x, y } = this;

    // The core, drawn as three rings of different diameters on the same frame.
    // One ring reads as a shockwave; three reads as a detonation with depth.
    emit(world, x, y, 0, 0, 0.10, 26, 'white', 'ring', 4.0, 0, 0);
    emit(world, x, y, 0, 0, 0.18, 74, 'white', 'ring', 3.0, 0, 0);
    emit(world, x, y, 0, 0, 0.30, 168, ROLE.exitPortal, 'ring', 2.4, 0, 0);
    // A wide white smoke disc under it all, which is what the bloom chain's
    // mid pyramid levels pick up and spread across the whole tube.
    emit(world, x, y, 0, 0, 0.22, 150, 'white', 'smoke', 2.6, 0.6, 0);

    for (let i = 0; i < BLAST_SPARKS; i++) {
      const ang = this.rand() * TAU;
      // Area-uniform, so the blast is a filled disc of debris and not a hoop.
      const spd = 60 + Math.sqrt(this.rand()) * 620;
      const white = i % 3 === 0;
      emit(world, x, y, Math.cos(ang) * spd, Math.sin(ang) * spd,
        0.3 + this.rand() * 0.9, 1.6 + this.rand() * 3.4,
        white ? 'white' : ROLE.exitPortal, 'spark',
        white ? 3.2 : 2.2, 1.4, 0);
    }

    shock(world, x, y, 460, 460, 0.3);
    world.fx.shake += 11;
  }

  /** Frames two to ten: two more rings, thrown off-centre, and a second punch. */
  private aftershock(world: World, t: number): void {
    if (t !== 3 && t !== 7) return;
    const ang = this.rand() * TAU;
    const d = 18 + this.rand() * 26;
    const x = this.x + Math.cos(ang) * d;
    const y = this.y + Math.sin(ang) * d;
    emit(world, x, y, 0, 0, 0.24, 60 + t * 9, 'white', 'ring', 2.4, 0, 0);
    for (let i = 0; i < 26; i++) {
      const a = this.rand() * TAU;
      const spd = 80 + this.rand() * 330;
      emit(world, x, y, Math.cos(a) * spd, Math.sin(a) * spd,
        0.24 + this.rand() * 0.5, 1.4 + this.rand() * 2.2,
        ROLE.exitPortal, 'spark', 2.4, 2.0, 0);
    }
    shock(world, x, y, 200, 300, 0.16);
    world.fx.shake += 3.4;
  }

  /**
   * The cascade: a wave of detonations running back up the corridor from the
   * exit, reaching the spawn at the same moment the blast's own light dies.
   *
   * Ordered by track progress, furthest along first, so it reads as the loss
   * propagating out of the breach rather than as the board being switched off.
   * Random order was tried first and is what it looks like — a hundred
   * simultaneous pops with no direction in them.
   *
   * Paced against the ticks remaining rather than at a fixed rate, so a board
   * holding four hundred swarmlings and a board holding six both take the same
   * fifth of a second. The scan is O(n) per body and the whole cascade is a
   * one-off on the last frames of a run; the alternative is sorting, and a sort
   * allocates.
   *
   * Each body plays its *own* family death, so the cascade is the death-variant
   * table firing a hundred times at once — the one moment in the game where
   * seeing all six compositions together is the point rather than the noise.
   */
  private cascade(world: World, t: number): void {
    const list = world.enemies;
    let n = Math.ceil(list.length / Math.max(1, CASCADE_TICKS - t));
    while (n-- > 0 && list.length > 0) {
      let best = 0;
      let bestAt = -Infinity;
      for (let i = 0; i < list.length; i++) {
        const p = list[i].progress;
        if (p <= bestAt) continue;
        bestAt = p;
        best = i;
      }
      this.hooks.vaporise(world, best);
    }
  }

  /**
   * The slow half.
   *
   * Bloom spikes on the blast frames and settles back over the next twenty; the
   * trail opens behind it and stays open until the sheet is up, then eases shut
   * so that whatever the player does next is played on the tuned tube. Both
   * terms land on their captured values exactly, not near them: a restore that
   * misses by a percent is a run that plays slightly wrong forever after.
   */
  private drivePost(t: number): void {
    const post = this.hooks.post;

    if (t <= GLOW_OPEN) {
      // Blast: hot and *short*. The trail is deliberately left alone here — a
      // long trail on the detonation frame smears the core into a blob and the
      // hit lands soft.
      const k = 1 - t / GLOW_OPEN;
      post.set('intensity', this.baseIntensity * (1 + (BLAST_BLOOM - 1) * k * k));
    } else if (t < GLOW_FULL) {
      const k = (t - GLOW_OPEN) / (GLOW_FULL - GLOW_OPEN);
      // Smoothstep, so the tube does not visibly step into its afterglow.
      const s = k * k * (3 - 2 * k);
      post.set('intensity', this.baseIntensity);
      post.set('persistence', this.basePersistence * (1 + (GLOW_GAIN - 1) * s));
      post.set('persistenceFloor', this.baseFloor + (GLOW_FLOOR - this.baseFloor) * s);
    } else if (t < REVEAL_TICK) {
      post.set('persistence', this.basePersistence * GLOW_GAIN);
    } else if (t < END_TICK) {
      const k = (t - REVEAL_TICK) / (END_TICK - REVEAL_TICK);
      const s = k * k * (3 - 2 * k);
      post.set('persistence', this.basePersistence * (GLOW_GAIN + (1 - GLOW_GAIN) * s));
      post.set('persistenceFloor', GLOW_FLOOR + (this.baseFloor - GLOW_FLOOR) * s);
    } else {
      post.set('persistence', this.basePersistence);
      post.set('intensity', this.baseIntensity);
      post.set('persistenceFloor', this.baseFloor);
    }
  }

  /**
   * What is left burning. Two or three embers a second, drifting up out of the
   * blast site on a long life — the only thing moving on a board that has
   * otherwise stopped, and the reason the afterglow reads as cooling rather
   * than as a still frame someone forgot to clear.
   */
  private embers(world: World, t: number): void {
    if (this.rand() > EMBER_CHANCE) return;
    const ang = this.rand() * TAU;
    const d = this.rand() * 90;
    const fade = 1 - (t - GLOW_OPEN) / (REVEAL_TICK - GLOW_OPEN);
    emit(world,
      this.x + Math.cos(ang) * d, this.y + Math.sin(ang) * d,
      (this.rand() * 2 - 1) * 16, -14 - this.rand() * 22,
      0.7 + this.rand() * 0.9, 1 + this.rand() * 1.8,
      this.rand() < 0.25 ? 'white' : ROLE.exitPortal, 'spark',
      0.7 + fade * 1.5, 0.9, 0);
  }

  /**
   * xorshift32 on the sequence's own state.
   *
   * Not `world.rng`: the sim's stream is shared with wave composition and spawn
   * jitter, and a defeat that drew four hundred numbers out of it would put a
   * restarted run on a different board than a replay of the same seed.
   */
  private rand(): number {
    let s = this.seed;
    s ^= s << 13; s >>>= 0;
    s ^= s >>> 17;
    s ^= s << 5; s >>>= 0;
    this.seed = s;
    return s / 4294967296;
  }
}
